import React from 'react';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { IDropdownOption } from '@fluentui/react';
import { Controller, UseFormMethods, useWatch } from 'react-hook-form';
import { useQuery } from '@apollo/client';
import TerminationLifecycleStateDropdown from '../../common/formFields/TerminationLifecycleStateDropdown';
import IDropdownReference from '../../../utils/types/IDropdownReference';
import { IEntityTerminationReviewApproval } from '../types';
import LegoCategoryDropdown from '../../common/formFields/LegoCategoryDropdown';
import { GET_TERMINATION_LIFECYCLE_STATUSES_QUERY } from '../../../utils/statApi/EntityStatusApi';

interface IEntityTerminationReviewApprovalFormProps {
  form: UseFormMethods<IEntityTerminationReviewApproval>;
  data: IEntityTerminationReviewApproval;
  setIsLegoCategoryRequired?: (required: boolean) => void;
}

const EntityTerminationReviewApprovalForm = ({
  form,
  data,
  setIsLegoCategoryRequired,
}: IEntityTerminationReviewApprovalFormProps) => {
  const { control, errors, setValue, getValues } = form;

  const onTerminationLifecycleStatusChange = (value: IDropdownOption): void => {
    setValue('terminationLifecycleStatus', value);
    setValue('legoCategory', null); // Reset Blocker Category selection
  };
  const onLegoCategoryChange = (value: IDropdownOption): void => {
    setValue('legoCategory', value);
  };

  const watchTerminationLifecycleStatus = useWatch({
    control,
    name: 'terminationLifecycleStatus',
    defaultValue: data?.terminationLifecycleStatus,
  });

  const isLegoCategoryRequired = watchTerminationLifecycleStatus?.fieldName === 'Blocker';

  if (setIsLegoCategoryRequired) {
    setIsLegoCategoryRequired(isLegoCategoryRequired);
  }

  const isBlockerDropdownDisabled = watchTerminationLifecycleStatus?.fieldName !== 'Blocker';

  const { loading, data: terminationLifecycleData } = useQuery(
    GET_TERMINATION_LIFECYCLE_STATUSES_QUERY,
  );

  const dropdownOptionsData =
    !loading && terminationLifecycleData
      ? (terminationLifecycleData.terminationLifecycleStatuses as IDropdownReference[]).map(
          (option) => ({
            ...option,
            isSelectable:
              option.fieldName === 'Blocker' ||
              option.fieldName === 'Eligible' ||
              option.fieldName === 'In Process',
          }),
        )
      : [];

  return (
    <Stack tokens={{ childrenGap: 10 }}>
      <Controller
        name="terminationLifecycleStatus"
        label="Termination Lifecycle Status"
        valueKey="terminationLifecycleStatus"
        control={control}
        render={(props: { value: IDropdownReference }) => {
          return (
            <TerminationLifecycleStateDropdown
              label="Termination Lifecycle Status"
              valueKey="terminationLifecycleStatus"
              required
              errors={errors?.terminationLifecycleStatus}
              handleChange={onTerminationLifecycleStatusChange}
              value={props.value}
              dropdownOptionsData={dropdownOptionsData}
            />
          );
        }}
        rules={{
          validate: () => {
            if (!getValues('terminationLifecycleStatus')?.fieldName?.trim()) {
              return 'Please select a Termination Lifecycle Status.';
            }
            return undefined;
          },
        }}
      />
      <Controller
        name="legoCategory"
        label="Blocker Category"
        valueKey="legoCategory"
        control={control}
        defaultValue={data?.legoCategory}
        render={(props: { value: IDropdownReference }) => {
          return (
            <LegoCategoryDropdown
              label="Blocker Category"
              valueKey="legoCategory"
              errors={errors?.legoCategory}
              handleChange={onLegoCategoryChange}
              value={props.value}
              watchTerminationLifecycleStatus={watchTerminationLifecycleStatus}
              required={isLegoCategoryRequired}
              onSelectableOptionsChange={undefined}
              disabled={isBlockerDropdownDisabled}
            />
          );
        }}
        rules={{
          validate: () => {
            if (isLegoCategoryRequired && !getValues('legoCategory')?.fieldName?.trim()) {
              return 'Please select a Blocker Category.';
            }
            return undefined;
          },
        }}
      />
    </Stack>
  );
};

export default EntityTerminationReviewApprovalForm;
